// src/components/pages/layout/SidebarAdmin.tsx
import { useNavigate, NavLink } from 'react-router-dom';
import {
  Shield,
  LogOut,
  Menu,
  Building,
  Users as UsersIcon,
  CreditCard,
  BarChart,
  Settings,
  ShieldAlert,
  Activity,
  ChevronRight,
  Users,
} from 'lucide-react';
import { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { logoutUser } from '@/components/utils/logoutUser';

const containerVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { duration: 0.3 } },
};
const submenuVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1, transition: { duration: 0.3 } },
  exit: { height: 0, opacity: 0, transition: { duration: 0.2 } },
};
const chevronVariants = {
  closed: { rotate: 0 },
  open: { rotate: 90, transition: { duration: 0.3 } },
};

const SidebarAdmin = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  const toggleMenu = (label: string) =>
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const handleChangeProfile = () => {
    navigate('/profiles');
    setMobileOpen(false);
  };

  const navItems = [
    {
      label: 'Dashboard',
      to: '/panel-admin',
      icon: <Activity className="w-5 h-5" />,
      end: true,
    },
    {
      label: 'Empresas',
      icon: <Building className="w-5 h-5" />,
      children: [
        { label: 'Listado', to: '/panel-admin/empresas' },
        { label: 'Auditoría', to: '/panel-admin/auditoria-empresas' },
      ],
    },
    {
      label: 'Usuarios',
      icon: <Users className="w-5 h-5" />,
      children: [
        { label: 'Listado', to: '/panel-admin/usuarios' },
        { label: 'Roles', to: '/panel-admin/roles' },
      ],
    },
    {
      label: 'Monetización',
      icon: <CreditCard className="w-5 h-5" />,
      children: [
        { label: 'Suscripciones', to: '/panel-admin/suscripciones' },
        { label: 'Pagos', to: '/panel-admin/pagos' },
      ],
    },
    {
      label: 'Estadísticas',
      icon: <BarChart className="w-5 h-5" />,
      to: '/panel-admin/estadisticas',
    },
    {
      label: 'Seguridad',
      icon: <ShieldAlert className="w-5 h-5" />,
      children: [
        { label: 'Lista negra', to: '/panel-admin/blacklist' },
        { label: 'Logs', to: '/panel-admin/logs' },
      ],
    },
    {
      label: 'Configuración',
      icon: <Settings className="w-5 h-5" />,
      to: '/panel-admin/config',
    },
  ];

  const renderNavItem = (item: any) => {
    if (item.children) {
      return (
        <div key={item.label}>
          <button
            onClick={() => !collapsed && toggleMenu(item.label)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition w-full text-left text-gray-600 hover:bg-gray-50"
          >
            {item.icon}
            {!collapsed && <span className="flex-1">{item.label}</span>}
            {!collapsed && (
              <motion.div
                variants={chevronVariants}
                animate={openMenus[item.label] ? 'open' : 'closed'}
              >
                <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
              </motion.div>
            )}
          </button>

          <AnimatePresence>
            {openMenus[item.label] && !collapsed && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={submenuVariants}
                className="mt-1 ml-4 flex flex-col gap-1 overflow-hidden"
              >
                {item.children.map((child: any) => renderNavItem(child))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <NavLink
        key={item.label}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          `flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition ${
            isActive
              ? 'bg-[#103254] text-white'
              : 'text-gray-600 hover:bg-gray-50'
          }`
        }
        onClick={() => setMobileOpen(false)}
      >
        {item.icon}
        {!collapsed && <span>{item.label}</span>}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile */}
      <button
        className="fixed top-4 right-4 z-50 p-2 rounded-full bg-white shadow-lg border lg:hidden"
        onClick={() => setMobileOpen(true)}
      >
        <Menu size={20} className="text-[#103254]" />
      </button>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-40 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 overflow-y-auto z-50 bg-white border-r shadow-xl
          transform transition-all duration-300
          ${collapsed ? 'w-20' : 'w-72'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:flex lg:flex-col lg:min-h-screen
        `}
      >
        <motion.div
          className="flex flex-col h-full p-4"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Botón colapsar */}
          <div className="flex justify-end mb-2">
            <button
              onClick={() => {
                setCollapsed((c) => !c);
                setTimeout(
                  () => window.dispatchEvent(new Event('resize')),
                  310
                );
              }}
              className="hidden lg:flex absolute top-6 -right-4 z-50 p-2 rounded-full bg-white shadow-lg border"
            >
              <ChevronRight
                className={`w-5 h-5 transition-transform ${
                  collapsed ? '' : 'rotate-180'
                }`}
              />
            </button>
          </div>

          {/* Header */}
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-3 p-3 mb-6 cursor-pointer"
            onClick={() => {
              navigate('/panel-admin');
              setMobileOpen(false);
            }}
          >
            <Shield className="w-10 h-10 text-[#103254]" />
            {!collapsed && (
              <div className="text-sm">
                <span className="font-medium text-gray-800">Administrador</span>
                <div className="text-gray-500 text-xs">Panel del sistema</div>
              </div>
            )}
          </motion.div>

          <div className="border-t my-4" />

          {/* Nav */}
          <nav className="flex flex-col gap-1 text-sm">
            {navItems.map((item) => renderNavItem(item))}
          </nav>

          {/* Footer botones */}
          <motion.div
            variants={itemVariants}
            className="mt-auto pt-6 border-t flex flex-col gap-2"
          >
            {!collapsed ? (
              <button
                onClick={handleChangeProfile}
                className="flex items-center gap-2 text-gray-600 hover:text-[#103254] text-sm px-2 py-2"
              >
                <UsersIcon className="w-5 h-5" />
                <span>Cambiar de perfil</span>
              </button>
            ) : (
              <button
                onClick={handleChangeProfile}
                className="flex items-center justify-center text-gray-600 hover:text-[#103254] text-sm px-2 py-2"
                title="Cambiar de perfil"
              >
                <UsersIcon className="w-5 h-5" />
              </button>
            )}

            {!collapsed ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-500 hover:text-red-600 text-sm px-2 py-2"
              >
                <LogOut className="w-5 h-5" />
                <span>Cerrar sesión</span>
              </button>
            ) : (
              <button
                onClick={handleLogout}
                className="flex items-center justify-center text-red-500 hover:text-red-600 text-sm px-2 py-2"
                title="Cerrar sesión"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </motion.div>
        </motion.div>
      </aside>
    </>
  );
};

export default memo(SidebarAdmin);
